/**
 * The one way an FTUX event leaves the tab (choke points C1 to C6, blueprint section 7): stamped with the run on screen
 * and the brand that run belongs to, then sent to ows-uxo by fetch (events/sendUxo). Synchronous, so a tab's events
 * are numbered (seq) in the order they are dispatched: a page's `did` before the `ended` of the hand-off it causes.
 */
import type { AppDispatch, RootState } from '@/store';

import { buildUxoEvent, nextUxoContext, type UxoEventInput } from '../events/envelope';
import { sendUxo } from '../events/sendUxo';

/** A thunk over the app's store, as the FTUX's transitions and events are dispatched. */
export type FtuxThunk<R = void> = (dispatch: AppDispatch, getState: () => RootState) => R;

/** One of the owner's brands, as the store holds them. */
type Brand = RootState['casinoInstance']['brands'][number];

/** The brand a run belongs to, by id: the displayed one, else from the owner's brands (a switch in flight). */
const brandOf = (state: RootState, brandId: string | null): Brand | undefined => {
  if (brandId === null) return undefined;
  const { displayBrand, brands } = state.casinoInstance;
  return displayBrand?.id === brandId ? displayBrand : brands.find((brand) => brand.id === brandId);
};

/**
 * Sends `input` as an event of the run on screen, stamped with that run's own brand, so a run never carries two
 * brands: Lumio's hello and the map are the owner's run, with no brand. Dropped when the layer is not mounted, when
 * there is no run yet, and while a support agent is signed in as the owner. Never throws.
 */
export const ftuxEvent =
  (input: UxoEventInput): FtuxThunk =>
  (_dispatch, getState) => {
    const state = getState();
    const run = state.ftux.mounted ? state.ftux.progress?.run : undefined;
    if (run === undefined || state.authentication.user.admin) return;
    const status = state.ftux.progress?.status;
    const brandId = status === 'hello' || status === 'lap' ? null : state.ftux.brandId;
    const brand = brandOf(state, brandId);
    const context = nextUxoContext({
      run,
      brandId,
      brandName: brand?.casino_data?.details?.brand ?? null,
      sessionId: state.session.sessionId,
    });
    sendUxo(buildUxoEvent(input, context));
  };
